'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// lib
import { IMemberOpportunity } from '@store/MemberOpportunities'
import { parseUtcDate } from '@utils/date'

/* -----------------------------------------------------------------------------
 * helpers
 * -------------------------------------------------------------------------- */

const detailOf = (opportunity: IMemberOpportunity, key: string) =>
  opportunity.opportunity_details?.[key]

const asNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const asText = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : null

// the API sends plain dates (`2026-09-11`), so they are read as UTC to keep
// the day from shifting in the viewer's zone
const formatDay = (value: unknown) =>
  parseUtcDate(asText(value) ?? undefined)?.format('MMM D, YYYY') ?? null

export const formatMoney = (value: number | null, fractionDigits = 0) =>
  value === null
    ? null
    : `$${value.toLocaleString('en-US', {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits
      })}`

const drugPair = (opportunity: IMemberOpportunity) => {
  const first = asText(detailOf(opportunity, 'primary_drug_a'))
  const second = asText(detailOf(opportunity, 'primary_drug_b'))

  return first && second ? `${first} and ${second}` : null
}

/* -----------------------------------------------------------------------------
 * describeOpportunity
 *
 * One line for the table, read off the fields each type carries.
 * -------------------------------------------------------------------------- */

export function describeOpportunity(opportunity: IMemberOpportunity): string {
  const lastFill = formatDay(opportunity.latest_fill_date)

  switch (opportunity.opportunity_type) {
    case 'copay_card': {
      const perFill = formatMoney(
        asNumber(detailOf(opportunity, 'per_fill_savings')),
        2
      )
      return perFill
        ? `Card takes ${perFill} off each fill`
        : 'Manufacturer copay card available'
    }
    case 'duplication_of_therapy': {
      const pair = drugPair(opportunity)
      const overlap = asNumber(detailOf(opportunity, 'max_overlap_days'))
      if (pair && overlap !== null) {
        return `${pair} overlap for up to ${overlap} days`
      }
      return pair ? `${pair} overlap` : 'Overlapping fills'
    }
    case 'new_to_therapy': {
      const started = formatDay(detailOf(opportunity, 'first_therapy_date'))
      const supply = asNumber(detailOf(opportunity, 'days_supply'))
      if (started && supply !== null) {
        return `Started ${started}, ${supply}-day supply`
      }
      return started ? `Started ${started}` : 'New prescription'
    }
    case '30_to_90_days_supply': {
      const supply = asNumber(detailOf(opportunity, 'days_supply'))
      if (supply !== null) {
        return `Filled as a ${supply}-day supply`
      }
      return lastFill
        ? `Eligible for a 90-day supply, last filled ${lastFill}`
        : 'Eligible for a 90-day supply'
    }
    default:
      return lastFill ? `Last filled ${lastFill}` : ''
  }
}

/* -----------------------------------------------------------------------------
 * buildChatMessage
 *
 * The message typed into the member's chat when the professional acts on an
 * opportunity. It is only a draft: the professional reviews it and sends it.
 * -------------------------------------------------------------------------- */

export function buildChatMessage(opportunity: IMemberOpportunity): string {
  const drug = asText(opportunity.drug_name) ?? 'your medication'
  const memberSavings = opportunity.estimated_annual_member_savings
  const yearly = memberSavings > 0 ? formatMoney(memberSavings) : null

  switch (opportunity.opportunity_type) {
    case 'copay_card': {
      const savings = yearly ? `, about ${yearly} a year` : ''
      return `Hi! I found a manufacturer copay card for ${drug} that could lower what you pay at the pharmacy${savings}. Use this link below to sign up!`
    }
    case '30_to_90_days_supply': {
      const savings = yearly ? ` and could save you about ${yearly} a year` : ''
      return `Hi! I noticed you refill ${drug} every month. Switching to a 90-day supply means fewer trips to the pharmacy${savings}. Would you like me to help you make the switch?`
    }
    case 'duplication_of_therapy': {
      const pair = drugPair(opportunity)
      // without the pair in the details the draft cannot name both drugs
      return pair
        ? `Hi! While reviewing your prescriptions I noticed you had both ${pair} filled recently. Are you currently taking these two together?`
        : `Hi! While reviewing your prescriptions I noticed overlapping fills of ${drug} recently. Are you currently taking these together?`
    }
    case 'new_to_therapy':
      return `Hi! I saw you recently started ${drug}. I am happy to answer any questions about how to take it or what to expect. What changes, if any, have you noticed in how you feel since starting?`
    default:
      return `Hi! I was reviewing your medications and wanted to check in about ${drug}. Do you have a few minutes to chat?`
  }
}
